import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronLeft } from 'lucide-react';

import { Button } from '@/Components/ui/button';
import { Skeleton } from '@/Components/ui/skeleton';
import { API_BASE_URL } from '@/config/api';

import useSEO from '../HomePage/useSEO';
import { PublicHeader, PublicFooter } from './PublicJobsPage';
import {
  ApplyAction,
  CompanyLogoMark,
  JobMetaRow,
  JobSections,
  PostedOn,
  useJobApplyFlow,
} from './PublicJobDetailDialog';

const JSONLD_ID = 'job-posting-jsonld';
const DEFAULT_TITLE = 'SearchJob.ai';

/**
 * /jobs/:jobId — one job on its own page.
 *
 * This is the URL shared links, the jobs feed and the sitemap point at, and
 * the page search engines read: it carries the job's own title, description,
 * canonical link and JobPosting structured data (built by the backend, so the
 * markup and the page can never disagree). Listing pages keep their inline
 * detail dialog for browsing; this page is the single-job destination.
 */
export default function PublicJobPage({ jobId }) {
  const navigate = useNavigate();
  // Keep `?subdomain=` (the local-dev tenant override) when leaving the page.
  const tenantQuery = (() => {
    const sub = new URLSearchParams(window.location.search).get('subdomain');
    return sub ? `?subdomain=${encodeURIComponent(sub)}` : '';
  })();
  const backToJobs = () => navigate(`/jobs${tenantQuery}`);

  const flow = useJobApplyFlow(jobId);
  const { data: job, loading, error, appliedInfo, onApplyClick } = flow;
  const seo = job?.seo || null;

  useSEO({
    title: job ? seo?.page_title || `${job.title} at ${job.company}` : undefined,
    description: seo?.meta_description,
    canonical: seo?.canonical_url,
    ogImage: seo?.og_image,
  });

  useJobHead({ structuredData: seo?.structured_data, noindex: Boolean(error) || Boolean(seo?.noindex) });

  const logoUrl = job?.company_logo
    ? `${API_BASE_URL}/api/hirewithus/company/logo/${job.company_logo}`
    : null;

  return (
    <div className="min-h-screen bg-slate-50/50 pb-20 sm:pb-0">
      <PublicHeader companyName={job?.company || null} companyLogoUrl={logoUrl} />

      <main className="mx-auto max-w-4xl px-4 py-6 sm:py-8">
        <Button variant="ghost" size="sm" onClick={backToJobs} className="-ml-2 mb-4 text-muted-foreground">
          <ChevronLeft className="mr-1 h-4 w-4" />
          All jobs
        </Button>

        {loading && <PageSkeleton />}

        {!loading && error && (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-border/60 bg-card px-6 py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <h1 className="text-lg font-semibold">This job is not available</h1>
            <p className="max-w-md text-sm text-muted-foreground">
              It may have been filled or closed. Browse the roles that are open right now.
            </p>
            <Button onClick={backToJobs}>Browse open jobs</Button>
          </div>
        )}

        {!loading && !error && job && (
          <article>
            <header className="rounded-xl border border-border/60 bg-card p-5 sm:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex min-w-0 items-start gap-3">
                  <CompanyLogoMark job={job} className="h-12 w-12" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-muted-foreground">{job.company || 'Company'}</p>
                    <h1 className="text-2xl font-bold tracking-tight text-foreground">{job.title}</h1>
                  </div>
                </div>
                {/* Desktop apply; phones get the sticky bar below. */}
                <ApplyAction applied={appliedInfo} onApply={onApplyClick} className="hidden shrink-0 sm:inline-flex" />
              </div>
              <JobMetaRow job={job} className="mt-4 text-sm" />
            </header>

            <div className="mt-4">
              <JobSections job={job} />
            </div>

            <footer className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/60 bg-card px-5 py-4">
              <PostedOn job={job} />
              <ApplyAction applied={appliedInfo} onApply={onApplyClick} />
            </footer>
          </article>
        )}
      </main>

      <PublicFooter />

      {/* Sticky apply bar — keeps the action reachable on phones. */}
      {!loading && !error && job && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border/60 bg-background/95 px-4 py-3 backdrop-blur sm:hidden">
          <ApplyAction applied={appliedInfo} onApply={onApplyClick} className="w-full" />
        </div>
      )}

      {flow.layers}
    </div>
  );
}

/**
 * Per-job <head> entries that useSEO does not cover: the JobPosting JSON-LD
 * script and the robots directive. Everything added here is removed when the
 * page unmounts so it cannot leak onto the next route of the SPA.
 */
function useJobHead({ structuredData, noindex }) {
  useEffect(() => {
    if (!structuredData) return undefined;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = JSONLD_ID;
    // JSON.stringify output placed in a text node is inert; "<" is escaped
    // anyway so the content can never close the script element.
    script.textContent = JSON.stringify(structuredData).replace(/</g, '\\u003c');
    document.getElementById(JSONLD_ID)?.remove();
    document.head.appendChild(script);
    return () => script.remove();
  }, [structuredData]);

  useEffect(() => {
    if (!noindex) return undefined;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    meta.setAttribute('data-job-page', 'true');
    document.head.appendChild(meta);
    return () => meta.remove();
  }, [noindex]);

  // Restore the generic title / canonical when leaving the job page.
  useEffect(() => {
    const previousTitle = document.title;
    return () => {
      document.title = previousTitle || DEFAULT_TITLE;
      document.head.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);
}

function PageSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading job">
      <Skeleton className="h-32 w-full rounded-xl" />
      <Skeleton className="h-40 w-full rounded-xl" />
      <Skeleton className="h-56 w-full rounded-xl" />
    </div>
  );
}
